import React, { useEffect, useState } from "react";
import { useAuthContext } from "../hooks/useAuthContext";
import { Link } from "react-router-dom";
import Loading from "../utils/Loading";
import { format } from "date-fns";
import ReactMarkdown from "react-markdown";
import { JOB_BASE_URL } from "../services/jobService";

const JobPortal = () => {
  const { user } = useAuthContext();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [selectedJob, setSelectedJob] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const jobsPerPage = 5;
  const [filters, setFilters] = useState({
    location: "",
    employmentType: "",
    remoteType: "",
  });

  useEffect(() => {
    const fetchJobs = async () => {
      try {
        const response = await fetch(`${JOB_BASE_URL}/api/jobs/list`);
        if (!response.ok) {
          throw new Error("Unable to display jobs!");
        }
        const data = await response.json();
        setJobs(data);
        if (data.length > 0) setSelectedJob(data[0]);
      } catch (error) {
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchJobs();
  }, []);

  const handleFilterChange = (e) => {
    setFilters({ ...filters, [e.target.name]: e.target.value });
    setCurrentPage(1); // Reset to first page when filters change
  };

  const filteredJobs = jobs.filter((job) => {
    return (
      (filters.location === "" ||
        job.location?.city
          ?.toLowerCase()
          .includes(filters.location.toLowerCase())) &&
      (filters.employmentType === "" ||
        job.employmentType === filters.employmentType) &&
      (filters.remoteType === "" || job.remoteType === filters.remoteType)
    );
  });

  if (loading) return <Loading />;
  if (error)
    return (
      <div className="container my-5">
        <div className="row">
          <div className="col-12">
            <div className="alert alert-danger text-center">
              <i className="fa fa-exclamation-triangle me-2"></i>
              {error}
            </div>
          </div>
        </div>
      </div>
    );

  const totalPages = Math.max(1, Math.ceil(filteredJobs.length / jobsPerPage));
  const indexOfLastJob = currentPage * jobsPerPage;
  const indexOfFirstJob = indexOfLastJob - jobsPerPage;
  const currentJobs = filteredJobs.slice(indexOfFirstJob, indexOfLastJob);

  return (
    <div className="container py-4">
      {/* Header Section */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="p-4 bg-primary text-white rounded-3 shadow">
            <h1 className="display-5 fw-bold mb-3">Job Opportunities</h1>
            <p className="lead mb-0">
              Browse through our latest job opportunities
            </p>
          </div>
        </div>
      </div>

      {/* Filtering Section */}
      <div className="row mb-4 g-3">
        <div className="col-md-4">
          <div className="input-group">
            <span className="input-group-text bg-white">
              <i className="fa fa-map-marker text-muted"></i>
            </span>
            <input
              type="text"
              name="location"
              placeholder="Location (city, state)"
              className="form-control"
              value={filters.location}
              onChange={handleFilterChange}
            />
          </div>
        </div>
        <div className="col-md-4">
          <div className="input-group">
            <span className="input-group-text bg-white">
              <i className="fa fa-briefcase text-muted"></i>
            </span>
            <select
              name="employmentType"
              className="form-select"
              value={filters.employmentType}
              onChange={handleFilterChange}
            >
              <option value="">All Job Types</option>
              <option value="Full-time">Full-time</option>
              <option value="Part-time">Part-time</option>
              <option value="Contract">Contract</option>
            </select>
          </div>
        </div>
        <div className="col-md-4">
          <div className="input-group">
            <span className="input-group-text bg-white">
              <i className="fa fa-home text-muted"></i>
            </span>
            <select
              name="remoteType"
              className="form-select"
              value={filters.remoteType}
              onChange={handleFilterChange}
            >
              <option value="">All Work Types</option>
              <option value="Remote">Remote</option>
              <option value="On-site">On-site</option>
              <option value="Hybrid">Hybrid</option>
            </select>
          </div>
        </div>
      </div>

      <div className="row g-4">
        {/* Job List */}
        <div className="col-lg-4">
          <div className="card border-0 shadow-sm h-100">
            <div className="card-header bg-white border-0">
              <h5 className="mb-0 fw-bold">
                <i className="fa fa-list-ul me-2"></i>
                Available Jobs ({filteredJobs.length})
              </h5>
            </div>
            <div className="card-body p-0" style={{ overflowY: "auto" }}>
              {filteredJobs.length === 0 ? (
                <div className="alert alert-warning m-3">
                  <i className="fa fa-info-circle me-2"></i>
                  No jobs found matching your criteria. Try adjusting your
                  filters.
                </div>
              ) : (
                <div className="list-group list-group-flush">
                  {currentJobs.map((job) => (
                    <div
                      key={job._id}
                      className={`list-group-item list-group-item-action p-3 ${
                        selectedJob?._id === job._id ? "active" : ""
                      }`}
                      onClick={() => setSelectedJob(job)}
                      style={{
                        cursor: "pointer",
                        borderLeft:
                          selectedJob?._id === job._id
                            ? "4px solid #0d6efd"
                            : "",
                      }}
                    >
                      <div className="d-flex w-100 justify-content-between">
                        <h6
                          className={`mb-1 ${
                            selectedJob?._id === job._id
                              ? "text-white"
                              : "text-primary"
                          }`}
                        >
                          {job.jobTitle}
                        </h6>
                        <small
                          className={
                            selectedJob?._id === job._id
                              ? "text-white-50"
                              : "text-muted"
                          }
                        >
                          <i className="fa fa-calendar me-1"></i>
                          {format(new Date(job.createdAt), "MMM d")}
                        </small>
                      </div>
                      <p
                        className={`mb-1 ${
                          selectedJob?._id === job._id
                            ? "text-white-50"
                            : "text-muted"
                        }`}
                      >
                        <i className="fa fa-building-o me-1"></i>
                        {job.companyName}
                      </p>
                      <div className="d-flex flex-wrap gap-2 mt-2">
                        <small
                          className={`badge ${
                            selectedJob?._id === job._id
                              ? "bg-light text-dark"
                              : "bg-light text-dark"
                          }`}
                        >
                          <i className="fa fa-map-marker me-1"></i>
                          {job.location?.city}, {job.location?.state}
                        </small>
                        <small
                          className={`badge ${
                            selectedJob?._id === job._id
                              ? "bg-light text-dark"
                              : "bg-light text-dark"
                          }`}
                        >
                          <i className="fa fa-briefcase me-1"></i>
                          {job.employmentType}
                        </small>
                        <small
                          className={`badge ${
                            selectedJob?._id === job._id
                              ? "bg-light text-dark"
                              : "bg-light text-dark"
                          }`}
                        >
                          <i className="fa fa-money me-1"></i>
                          {new Intl.NumberFormat("en-US", {
                            style: "currency",
                            currency: "USD",
                            maximumFractionDigits: 0,
                          }).format(job.salary)}
                        </small>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            {/* Pagination */}
            {filteredJobs.length > 0 && (
              <div className="card-footer bg-white border-0">
                <nav aria-label="Job pagination">
                  <ul className="pagination pagination-sm justify-content-center mb-0">
                    <li
                      className={`page-item ${
                        currentPage === 1 ? "disabled" : ""
                      }`}
                    >
                      <button
                        className="page-link"
                        onClick={() => setCurrentPage(currentPage - 1)}
                        disabled={currentPage === 1}
                      >
                        <i className="fa fa-chevron-left"></i> Previous
                      </button>
                    </li>
                    {[...Array(totalPages)].map((_, index) => (
                      <li
                        key={index}
                        className={`page-item ${
                          currentPage === index + 1 ? "active" : ""
                        }`}
                      >
                        <button
                          className="page-link"
                          onClick={() => setCurrentPage(index + 1)}
                        >
                          {index + 1}
                        </button>
                      </li>
                    ))}
                    <li
                      className={`page-item ${
                        currentPage === totalPages ? "disabled" : ""
                      }`}
                    >
                      <button
                        className="page-link"
                        onClick={() => setCurrentPage(currentPage + 1)}
                        disabled={currentPage === totalPages}
                      >
                        Next <i className="fa fa-chevron-right"></i>
                      </button>
                    </li>
                  </ul>
                </nav>
              </div>
            )}
          </div>
        </div>

        {/* Job Details */}
        <div className="col-lg-8">
          {selectedJob && filteredJobs.length > 0 ? (
            <div className="card border-0 shadow-sm h-100">
              <div className="card-header bg-white border-0">
                <div className="d-flex justify-content-between align-items-center">
                  <h4 className="mb-0 fw-bold">
                    <i className="fa fa-file-text-o me-2"></i>
                    {selectedJob.jobTitle}
                  </h4>
                  <Link
                    to={`/api/jobs/job-details/${
                      selectedJob._id
                    }/${selectedJob.jobTitle.replace(/\s+/g, "-")}`}
                    className="btn btn-primary btn-sm"
                  >
                    <i className="fa fa-eye me-1"></i> View Full Details
                  </Link>
                </div>
              </div>
              <div className="card-body">
                <div className="mb-4">
                  <h5 className="text-primary">
                    <i className="fa fa-building-o me-2"></i>
                    {selectedJob.companyName}
                  </h5>
                  <div className="d-flex flex-wrap gap-3 mt-3">
                    <div className="d-flex align-items-center text-muted">
                      <i className="fa fa-map-marker me-2"></i>
                      <span>
                        {selectedJob.location?.city},{" "}
                        {selectedJob.location?.state}
                      </span>
                    </div>
                    <div className="d-flex align-items-center text-muted">
                      <i className="fa fa-briefcase me-2"></i>
                      <span>{selectedJob.employmentType}</span>
                    </div>
                    <div className="d-flex align-items-center text-muted">
                      <i className="fa fa-home me-2"></i>
                      <span>{selectedJob.remoteType}</span>
                    </div>
                    <div className="d-flex align-items-center text-muted">
                      <i className="fa fa-money me-2"></i>
                      <span>
                        {new Intl.NumberFormat("en-US", {
                          style: "currency",
                          currency: "USD",
                        }).format(selectedJob.salary)}
                      </span>
                    </div>
                    <div className="d-flex align-items-center text-muted">
                      <i className="fa fa-calendar me-2"></i>
                      <span>
                        Posted:{" "}
                        {format(
                          new Date(selectedJob.createdAt),
                          "MMMM d, yyyy"
                        )}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="mb-4">
                  <h6 className="fw-bold border-bottom pb-2 mb-3">
                    <i className="fa fa-align-left me-2"></i>
                    Job Description
                  </h6>
                  <div className="text-muted">
                    <ReactMarkdown children={selectedJob.description} />
                  </div>
                </div>

                {/* <div className="d-grid gap-2 d-md-flex justify-content-md-end mt-4">
                  <button className="btn btn-outline-primary me-md-2">
                    <i className="fa fa-bookmark me-1"></i> Save Job
                  </button>
                  <button className="btn btn-primary">
                    <i className="fa fa-paper-plane me-1"></i> Apply Now
                  </button>
                </div> */}
              </div>
            </div>
          ) : (
            <div className="card border-0 shadow-sm h-100">
              <div className="card-body d-flex align-items-center justify-content-center">
                <div className="text-center">
                  <i className="fa fa-search fa-3x text-muted mb-3"></i>
                  <h5 className="text-muted">Select a job to view details</h5>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default JobPortal;
